import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import Link from 'next/link';
import { Box } from '@mui/material';
import Typography from '@mui/material/Typography';
import PaymentService from 'services/PaymentService';
import PlanField from 'components/PlanField';
import Button from 'components/Button';
import AlertAlt from 'components/AlertAlt';
import {
  Card,
  TitleWrapper,
  Title,
  Separator,
  PlanFieldContainer,
} from 'styles/components/planDetailsCard';
import { flowTrialActive } from 'utils';
import StarterPlanCardAlternative from 'components/StarterPlanDetailsCardAlternative';
import { checkUserCountry } from 'utils/locale';
import LoadingCircle from 'components/LoadingCircle';
import { useAuthUser } from 'features/auth/AuthUserContext';

export default function StarterPlanCard() {
  const router = useRouter();
  const { user, isAuthenticated } = useAuthUser();
  const paymentService = new PaymentService();
  const [currentSubscription, setCurrentSubscription] = useState({});
  const [latestInvoice, setLatestInvoice] = useState(null);
  const isInvoiceOpen = latestInvoice?.status === 'open' && latestInvoice?.attempt_count === 0;
  const [userIsChina, setUserIsChina] = useState(false);
  const [userIsJapan, setUserIsJapan] = useState(false);
  const [isLoadingUserData, setIsLoadingUserData] = useState(true);

  const isInvoiceError =
    latestInvoice?.status === 'unpaid' ||
    (latestInvoice?.attempt_count > 0 && latestInvoice?.status !== 'paid');

  async function getCountryFromUser() {
    const isChina = await checkUserCountry.isChina(user);
    const isJapan = await checkUserCountry.isJapan(user);

    setUserIsChina(isChina);
    setUserIsJapan(isJapan);
  }

  useEffect(() => {
    async function fetchStripeSubscription() {
      try {
        const response = await paymentService.getStripeSubscription();

        if (response.data) {
          setCurrentSubscription(response.data);
          setIsLoadingUserData(false);
        }
      } catch (e) {
        console.error(e);
      }
    }

    if (isAuthenticated) {
      fetchStripeSubscription();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    setLatestInvoice(currentSubscription.latest_invoice);
    setIsLoadingUserData(false);
  }, [currentSubscription]);

  useEffect(() => {
    getCountryFromUser();
  }, [user]);

  if (isLoadingUserData) {
    return <LoadingCircle />;
  }

  if (flowTrialActive && !(userIsChina || userIsJapan)) {
    return <StarterPlanCardAlternative />;
  }

  return (
    <Card>
      <TitleWrapper>
        <Title margin="0">Starter Subscription</Title>

        {isInvoiceError && (
          <Box>
            <AlertAlt severity="error">
              Alert:{' '}
              <Link href="/my-account/billing/update-card" passHref>
                Credit card update
              </Link>{' '}
              required.
            </AlertAlt>
          </Box>
        )}

        {isInvoiceOpen && (
          <Box>
            <AlertAlt severity="warning">
              You have an open invoice.{' '}
              <Link href="/my-account/billing/update-card" passHref>
                Click here
              </Link>{' '}
              to confirm the subscription.
            </AlertAlt>
          </Box>
        )}
      </TitleWrapper>

      <Separator />

      <PlanFieldContainer>
        <PlanField icon="account_balance_wallet" label="Amount" value="Free" />
      </PlanFieldContainer>

      <Box p={2} bgcolor="grey.50">
        <Typography gutterBottom={true}>
          <b>Looking for the perfect plan for you?</b>
        </Typography>
        <span style={{ width: 'max-content', display: 'block' }}>
          <Button variant="primary" isInline={true} onClick={() => router.push('/pricing')}>
            Check Available Plans
          </Button>
        </span>
      </Box>
    </Card>
  );
}
